import { useState } from 'react';
import { Grid3x3 } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { StatusPill } from '@siliconx/shared/ui/StatusPill';
import type { EnvironmentalBasis } from '@siliconx/shared';
import {
  useKpiScope3,
  useScope3BuyerLens,
  useScope3Cat4,
  useScope3Cat11,
  useScope3Cat12,
} from '../../hooks/useEnvironmentalOverview';
import { BasisTag } from './BasisTag';
import { formatTonnes } from './format';
import { RegulationChip } from './RegulationChip';
import { SCOPE3_MATRIX_ROWS, type Scope3Applicability } from './scope3Copy';

/** SIL-611 — inline regulation chip per disposal/EOL category row. Category
 * 5 (waste generated in operations / disposal) cites the battery-specific
 * universal waste rule (its register row explicitly names the "Cat-5
 * disposal flow"); Category 12 (end-of-life of sold products) cites the
 * general California UWED rule. Never on any other row — those are not
 * disposal/EOL flows. */
const CATEGORY_REGULATION_CHIP: Partial<Record<string, 'universal_waste_battery' | 'ca_uwed'>> = {
  '5': 'universal_waste_battery',
  '12': 'ca_uwed',
};

/**
 * SIL-488 — Overview tab's Scope 3 section, v1: the Variant B category x
 * persona matrix (SIL-464 prototype), replacing the v0 ledger
 * (`Scope3UpstreamDownstreamSection`, removed this same change). Authority
 * for rows/applicability/notes: `resources/plans/sil-463-scope3-category-
 * matrix.md`; row data lives in `scope3Copy.ts`'s `SCOPE3_MATRIX_ROWS`,
 * never hand-derived here.
 *
 * All 15 GHG Protocol categories (1/2 merged) x three personas — seller /
 * buying new / buying used (SiliconX). Seller is the org's own lens and the
 * only column with real figures today (Cat 1/2 reuses the overview's Scope
 * 3 KPI, Cat 4/11/12 reuse the v0 section's hooks); Cat 8/9 are IN per the
 * matrix but have no serving surface yet, so they render applicability
 * badges even in the seller column. SIL-491: buying-new/buying-used now
 * render REAL values + basis tags for Cat 1/2 and Cat 4 (the org's own
 * fleet split by acquisition channel via `asset.previous_asset_id` —
 * `useScope3BuyerLens`); every other row (including Cat 8, whose colo split
 * has no per-acquisition data) keeps the applies/depends badge in both
 * buyer columns. Excluded rows (OUT verdict) render dimmed; their
 * expand affordance carries the exclusion-justification string instead of a
 * computation note. The full exclusion register (S3 ch11 required
 * reporting) moved to the Reports tab (SIL-477) — this component never
 * renders the whole list, only a per-row justification on expand.
 *
 * NO NETTING, NO AVOIDED NUMBERS — footprint side of the SIL-426 firewall.
 * The dashboard-wide R0 framing line renders once, tab-wide, in
 * `EnvironmentalFooter`.
 */
export function Scope3CategoryMatrix() {
  const cat1_2 = useKpiScope3();
  const cat4 = useScope3Cat4();
  const cat11 = useScope3Cat11();
  const cat12 = useScope3Cat12();
  const buyerLens = useScope3BuyerLens();
  const [expanded, setExpanded] = useState<string | null>(null);

  const sellerValueByCategory: Record<string, { value: number | null; basis: ReturnType<typeof useKpiScope3>['basis'] }> = {
    '1/2': { value: cat1_2.data ?? null, basis: cat1_2.basis },
    '4': { value: cat4.data ?? null, basis: cat4.basis },
    '11': { value: cat11.data.valueKgco2e, basis: cat11.basis },
    '12': { value: cat12.data.valueKgco2e, basis: cat12.basis },
  };

  // SIL-491 — buyer-lens value maps, Cat 1/2 + Cat 4 only. `undefined`
  // (never a zeroed figure) while the shared query is still loading — the
  // row falls back to `ApplicabilityCell`'s badge until real data arrives,
  // same "no fabricated interim number" convention as `SellerCell` below.
  const buyerNewValueByCategory: Record<string, { value: number; basis: EnvironmentalBasis } | undefined> = {
    '1/2': buyerLens.data && { value: buyerLens.data.newCat1_2Kgco2e.valueKgco2e, basis: buyerLens.data.newCat1_2Kgco2e.basis },
    '4': buyerLens.data && { value: buyerLens.data.newCat4Kgco2e.valueKgco2e, basis: buyerLens.data.newCat4Kgco2e.basis },
  };
  const buyerUsedValueByCategory: Record<string, { value: number; basis: EnvironmentalBasis } | undefined> = {
    '1/2': buyerLens.data && { value: buyerLens.data.usedCat1_2Kgco2e.valueKgco2e, basis: buyerLens.data.usedCat1_2Kgco2e.basis },
    '4': buyerLens.data && { value: buyerLens.data.usedCat4Kgco2e.valueKgco2e, basis: buyerLens.data.usedCat4Kgco2e.basis },
  };

  return (
    <Card padding="md" data-testid="scope3-matrix">
      <div className="mb-1 flex items-center gap-2">
        <Grid3x3 className="size-4 text-zinc-400" />
        <SectionLabel>Scope 3 · category applicability by role</SectionLabel>
      </div>
      <p className="mb-3 text-xs leading-relaxed text-zinc-400">
        All 15 GHG Protocol Scope 3 categories, and whether each applies to your organization as a
        seller on the platform, a buyer of new hardware, or a buyer of used hardware from SiliconX.
        Seller figures are your organization's own estimates; click a row for its method note.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-zinc-200 text-xs font-semibold uppercase tracking-widest text-zinc-400">
              <th className="px-2 py-1.5 text-left font-semibold">Category</th>
              <th className="px-2 py-1.5 text-center font-semibold">As seller</th>
              <th className="px-2 py-1.5 text-center font-semibold">Buying new</th>
              <th className="px-2 py-1.5 text-center font-semibold">Buying used (SiliconX)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {SCOPE3_MATRIX_ROWS.map((row) => {
              const isExpanded = expanded === row.category;
              const sellerFigure = sellerValueByCategory[row.category];
              return (
                <MatrixRowGroup
                  key={row.category}
                  row={row}
                  sellerFigure={sellerFigure}
                  buyerNewFigure={buyerNewValueByCategory[row.category]}
                  buyerUsedFigure={buyerUsedValueByCategory[row.category]}
                  isExpanded={isExpanded}
                  onToggle={() => setExpanded(isExpanded ? null : row.category)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-zinc-400">
        Excluded categories are disclosed with justification consistent with the GHG Protocol Scope
        3 Standard; the full exclusion register lives on the Reports tab.
      </p>
    </Card>
  );
}

function MatrixRowGroup({
  row,
  sellerFigure,
  buyerNewFigure,
  buyerUsedFigure,
  isExpanded,
  onToggle,
}: {
  row: (typeof SCOPE3_MATRIX_ROWS)[number];
  sellerFigure: { value: number | null; basis: ReturnType<typeof useKpiScope3>['basis'] } | undefined;
  buyerNewFigure: { value: number; basis: EnvironmentalBasis } | undefined;
  buyerUsedFigure: { value: number; basis: EnvironmentalBasis } | undefined;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`cursor-pointer ${row.excluded ? 'opacity-50' : ''}`}
        onClick={onToggle}
        data-testid={`scope3-matrix-row-${row.category}`}
        title={row.note}
      >
        <td className="px-2 py-1.5">
          <span className="mr-1.5 inline-block w-8 font-mono text-[13px] text-zinc-400">{row.category}</span>
          <span className="text-xs text-zinc-800">{row.shortName}</span>
          {row.excluded && (
            <span className="ml-1.5 rounded bg-zinc-100 px-1 py-0.5 text-xs uppercase tracking-wide text-zinc-500">
              excluded
            </span>
          )}
          {CATEGORY_REGULATION_CHIP[row.category] && (
            <span className="ml-1.5 inline-block" onClick={(e) => e.stopPropagation()}>
              <RegulationChip regulationKey={CATEGORY_REGULATION_CHIP[row.category]!} />
            </span>
          )}
        </td>
        <SellerCell row={row} figure={sellerFigure} />
        <BuyerCell a={row.buyerNew} figure={buyerNewFigure} />
        <BuyerCell a={row.buyerUsed} figure={buyerUsedFigure} />
      </tr>
      {isExpanded && (
        <tr data-testid={`scope3-matrix-note-${row.category}`}>
          <td colSpan={4} className="bg-zinc-50 px-2 py-2 text-xs leading-relaxed text-zinc-500">
            {row.note}
          </td>
        </tr>
      )}
    </>
  );
}

/** Seller column: real value + basis tag when a serving hook exists for the
 * category (Cat 1/2, 4, 11, 12 today); otherwise the same applicability
 * badge as the buyer columns (Cat 8/9 — IN per the matrix, no hook yet). */
function SellerCell({
  row,
  figure,
}: {
  row: (typeof SCOPE3_MATRIX_ROWS)[number];
  figure: { value: number | null; basis: ReturnType<typeof useKpiScope3>['basis'] } | undefined;
}) {
  if (row.excluded) return <td className="px-2 py-1.5 text-center text-zinc-300">—</td>;
  if (figure) {
    return (
      <td className="px-2 py-1.5 text-center">
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold tabular-nums text-zinc-900">
            {formatTonnes(figure.value)}
          </span>
          <BasisTag basis={figure.basis} />
        </span>
      </td>
    );
  }
  return <ApplicabilityCell a={row.seller} />;
}

/** Buying-new / buying-used columns (SIL-491): real value + basis tag when
 * a buyer-lens figure exists for the row (Cat 1/2, Cat 4 today — the org's
 * own fleet split by acquisition channel); otherwise the same applicability
 * badge Cat 8/9/etc. render. Never fabricates a number while the shared
 * query is loading — `figure` is `undefined` until real data resolves, and
 * the row shows its badge in the interim (never a flashed zero). */
function BuyerCell({
  a,
  figure,
}: {
  a: Scope3Applicability;
  figure: { value: number; basis: EnvironmentalBasis } | undefined;
}) {
  if (figure) {
    return (
      <td className="px-2 py-1.5 text-center">
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono text-xs font-semibold tabular-nums text-zinc-900">
            {formatTonnes(figure.value)}
          </span>
          <BasisTag basis={figure.basis} />
        </span>
      </td>
    );
  }
  return <ApplicabilityCell a={a} />;
}

/** Fallback badge for a buyer-column cell with no serving figure (Cat
 * 8/9/etc.) — 'depends'/'applies' pills, or a dash for 'no'. */
function ApplicabilityCell({ a }: { a: Scope3Applicability }) {
  if (a === 'no') return <td className="px-2 py-1.5 text-center text-zinc-300">—</td>;
  return (
    <td className="px-2 py-1.5 text-center">
      <StatusPill tone={a === 'depends' ? 'amber' : 'green'} className="text-xs">
        {a === 'depends' ? 'depends' : 'applies'}
      </StatusPill>
    </td>
  );
}
